import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Reply-token countdown banner ──────────────────────
// Backend exposes conversation.reply_expires_at (ISO or null). Warn the agent
// when the reply window is closing so they aren't surprised by a 409 on Send.
// Levels: info (5–15 min), warn (2–5 min), urgent (<2 min), expired (null/0).
function ReplyExpiryBanner({ expiresAt, nowTs }) {
  if (!expiresAt) {
    return (
      <div className="reply-banner reply-banner-expired">
        ⛔ หมดเวลาตอบแล้ว — รอลูกค้าส่งข้อความใหม่ก่อนถึงจะตอบได้ (ประหยัด quota)
      </div>
    );
  }
  const remainMs = new Date(expiresAt).getTime() - nowTs;
  if (remainMs <= 0) {
    return (
      <div className="reply-banner reply-banner-expired">
        ⛔ หมดเวลาตอบแล้ว — รอลูกค้าส่งข้อความใหม่ก่อนถึงจะตอบได้ (ประหยัด quota)
      </div>
    );
  }
  const totalSec = Math.floor(remainMs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min >= 15) return null; // safely inside the window, no banner clutter
  const level  = min < 2 ? 'urgent' : min < 5 ? 'warn' : 'info';
  const timeStr = min > 0 ? `${min}:${String(sec).padStart(2, '0')} นาที` : `${sec} วินาที`;
  return (
    <div className={`reply-banner reply-banner-${level}`}>
      ⏰ เหลือเวลาตอบอีก <strong>{timeStr}</strong> ก่อนหมดสิทธิ์ตอบฟรี — รีบตอบเพื่อไม่ต้องรอลูกค้าใหม่
    </div>
  );
}

// ── Date separator label (LINE-style) ─────────────────
const TH_MONTHS = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];
function formatDateSeparator(iso) {
  const d = dayjs(iso);
  const today = dayjs().startOf('day');
  if (d.isSame(today, 'day')) return 'วันนี้';
  if (d.isSame(today.subtract(1, 'day'), 'day')) return 'เมื่อวาน';
  if (d.year() === dayjs().year()) return `${d.date()} ${TH_MONTHS[d.month()]}`;
  return `${d.date()} ${TH_MONTHS[d.month()]} ${d.year() + 543}`;
}

// ── Auto-parse customer info from message text ────────
function parseCustomerInfo(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const phoneRx   = /^0[6-9]\d{8}$/;
  const accountRx = /(\d{10,12})/;
  const bankNames = ['SCB','กสิกร','KBANK','กรุงเทพ','BBL','กรุงไทย','KTB',
    'ทหารไทย','TTB','ออมสิน','GSB','ธ.ก.ส','BAAC','UOB','CIMB','LH',
    'ทิสโก้','TISCO','เกียรตินาคิน','KKP','อิออน','ซิตี้'];

  let name = '', phone = '';
  const bankAccounts = [];

  for (const line of lines) {
    const noSpace = line.replace(/[-\s]/g, '');
    if (!phone && phoneRx.test(noSpace)) {
      phone = noSpace;
      continue;
    }
    const acctMatch = line.match(accountRx);
    if (acctMatch) {
      const accountNumber = acctMatch[1];
      const prefix = line.slice(0, line.indexOf(accountNumber)).replace(/[-\s]/g, '').trim();
      const bankName = bankNames.find(b => prefix.toUpperCase().includes(b.toUpperCase())) || prefix || 'ไม่ระบุ';
      bankAccounts.push({ bank_name: bankName, account_number: accountNumber });
      continue;
    }
    if (!name) name = line;
  }
  return { name, phone, bankAccounts };
}

const FLEX_TEMPLATES = [
  {
    key: 'welcome', label: '👋 Welcome',
    altText: 'สวัสดีค่ะ ยินดีต้อนรับ',
    contents: {
      type: 'bubble',
      body: { type: 'box', layout: 'vertical', spacing: 'md',
        contents: [
          { type: 'text', text: 'สวัสดีค่ะ 👋', size: 'xl', weight: 'bold', color: '#16A34A' },
          { type: 'text', text: 'ยินดีต้อนรับเข้าสู่บริการของเรา', size: 'sm', color: '#6B7280', wrap: true },
          { type: 'text', text: 'ติดต่อเจ้าหน้าที่ได้ตลอด 24 ชั่วโมง', size: 'xs', color: '#9CA3AF', wrap: true }
        ]
      }
    }
  },
  {
    key: 'promo', label: '🎁 โปรโมชั่น',
    altText: '🎁 โปรโมชั่นพิเศษวันนี้',
    contents: {
      type: 'bubble',
      hero: { type: 'image',
        url: 'https://placehold.co/1040x520/16a34a/ffffff/png?text=PROMOTION',
        size: 'full', aspectRatio: '20:13', aspectMode: 'cover'
      },
      body: { type: 'box', layout: 'vertical', spacing: 'md',
        contents: [
          { type: 'text', text: '🎁 โปรโมชั่นพิเศษ', weight: 'bold', size: 'lg', color: '#16A34A' },
          { type: 'text', text: 'เปิดบัญชีใหม่รับโบนัส 100% สูงสุด 5,000 บาท', size: 'sm', color: '#374151', wrap: true }
        ]
      },
      footer: { type: 'box', layout: 'vertical',
        contents: [
          { type: 'button', style: 'primary', color: '#16A34A', height: 'sm',
            action: { type: 'message', label: 'รับเลย', text: 'ขอรับโปรโมชั่นค่ะ' } }
        ]
      }
    }
  },
  {
    key: 'menu', label: '📋 เมนู 3 ปุ่ม',
    altText: 'เลือกบริการที่ต้องการ',
    contents: {
      type: 'bubble', size: 'kilo',
      body: { type: 'box', layout: 'vertical', spacing: 'sm',
        contents: [
          { type: 'text', text: 'เลือกบริการที่ต้องการ', weight: 'bold', size: 'md' },
          { type: 'separator', margin: 'md' },
          { type: 'button', style: 'secondary', height: 'sm', margin: 'md',
            action: { type: 'message', label: '💰 เช็คยอด', text: 'เช็คยอด' } },
          { type: 'button', style: 'secondary', height: 'sm',
            action: { type: 'message', label: '📥 ฝากเงิน', text: 'ฝากเงิน' } },
          { type: 'button', style: 'primary', color: '#16A34A', height: 'sm',
            action: { type: 'message', label: '💬 ติดต่อแอดมิน', text: 'ขอคุยกับแอดมิน' } }
        ]
      }
    }
  },
  {
    key: 'receipt', label: '🧾 ใบเสร็จ',
    altText: 'ฝากเงินสำเร็จ',
    contents: {
      type: 'bubble',
      body: { type: 'box', layout: 'vertical',
        contents: [
          { type: 'text', text: 'RECEIPT', weight: 'bold', color: '#16A34A', size: 'sm' },
          { type: 'text', text: 'ฝากเงินสำเร็จ', weight: 'bold', size: 'xl', margin: 'sm' },
          { type: 'separator', margin: 'lg' },
          { type: 'box', layout: 'vertical', margin: 'lg', spacing: 'sm', contents: [
            { type: 'box', layout: 'horizontal', contents: [
              { type: 'text', text: 'จำนวน', size: 'sm', color: '#6B7280', flex: 2 },
              { type: 'text', text: '1,000.00 บาท', size: 'sm', color: '#111827', align: 'end', flex: 3, weight: 'bold' }
            ]},
            { type: 'box', layout: 'horizontal', contents: [
              { type: 'text', text: 'อ้างอิง', size: 'sm', color: '#6B7280', flex: 2 },
              { type: 'text', text: 'TX-001', size: 'xs', color: '#9CA3AF', align: 'end', flex: 3 }
            ]}
          ]}
        ]
      }
    }
  },
  {
    key: 'carousel', label: '🎠 Carousel',
    altText: 'โปรโมชั่นวันนี้',
    contents: {
      type: 'carousel',
      contents: [
        { type: 'bubble', size: 'micro',
          body: { type: 'box', layout: 'vertical', contents: [
            { type: 'text', text: '🎁 โบนัส 50%', weight: 'bold', color: '#16A34A' },
            { type: 'text', text: 'เปิดบัญชีใหม่', size: 'xs', color: '#6B7280', margin: 'sm' }
          ]},
          footer: { type: 'box', layout: 'vertical', contents: [{
            type: 'button', style: 'primary', color: '#16A34A', height: 'sm',
            action: { type: 'message', label: 'รับเลย', text: 'รับโปร 50%' }
          }]}
        },
        { type: 'bubble', size: 'micro',
          body: { type: 'box', layout: 'vertical', contents: [
            { type: 'text', text: '💰 คืน 5%', weight: 'bold', color: '#2563EB' },
            { type: 'text', text: 'ทุกการฝากวันนี้', size: 'xs', color: '#6B7280', margin: 'sm' }
          ]},
          footer: { type: 'box', layout: 'vertical', contents: [{
            type: 'button', style: 'primary', color: '#2563EB', height: 'sm',
            action: { type: 'message', label: 'รับเลย', text: 'รับโปรคืน 5%' }
          }]}
        }
      ]
    }
  }
];

function FlexModal({ conversation, onClose, onSent }) {
  const [altText, setAltText] = useState('');
  const [json, setJson]       = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState(null); // { message, details[] }

  function applyTemplate(tpl) {
    setAltText(tpl.altText);
    setJson(JSON.stringify(tpl.contents, null, 2));
    setError(null);
  }

  async function send() {
    setSending(true); setError(null);
    try {
      if (!altText.trim()) throw new Error('กรุณากรอก Alt Text');
      if (!json.trim())    throw new Error('กรุณาเลือก Template หรือกรอก Flex JSON');
      let contents;
      try { contents = JSON.parse(json); }
      catch (e) { throw new Error('JSON ไม่ถูกต้อง: ' + e.message); }
      if (!contents.type || (contents.type !== 'bubble' && contents.type !== 'carousel')) {
        throw new Error('JSON ต้องขึ้นต้นด้วย type: "bubble" หรือ "carousel"');
      }
      const msg = await api.sendFlexMessage(conversation.id, { altText: altText.trim(), contents });
      onSent?.(msg);
      onClose();
    } catch (err) {
      setError({ message: err.message, details: err.details, lineStatus: err.lineStatus });
    } finally { setSending(false); }
  }

  return (
    <div className="image-send-overlay" onClick={onClose}>
      <div className="image-send-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>🎴 ส่ง Flex Message</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              📐 เลือก Template เพื่อเริ่มต้น
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {FLEX_TEMPLATES.map(tpl => (
                <button key={tpl.key}
                  onClick={() => applyTemplate(tpl)}
                  style={{
                    padding: '6px 12px', fontSize: 12,
                    background: 'var(--bg-panel)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  }}>
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-field">
            <label>Alt Text (แสดงใน notification)</label>
            <input className="form-input" value={altText} onChange={e => setAltText(e.target.value)}
              placeholder="เช่น โปรโมชั่นพิเศษ" />
          </div>
          <div className="form-field">
            <label>Flex JSON (contents object)</label>
            <textarea className="form-input" rows={10} value={json}
              onChange={e => setJson(e.target.value)}
              placeholder={'เลือก Template ด้านบน หรือ paste JSON เอง...'}
              style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              💡 ออกแบบเองที่ <a href="https://developers.line.biz/flex-simulator/" target="_blank" rel="noreferrer">LINE Flex Simulator</a>
            </div>
          </div>

          {error && (
            <div style={{ marginTop: 10, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
              <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>❌ {error.message}</div>
              {error.lineStatus && (
                <div style={{ fontSize: 11, color: '#991b1b', marginTop: 2 }}>LINE API status: {error.lineStatus}</div>
              )}
              {error.details && Array.isArray(error.details) && error.details.length > 0 && (
                <ul style={{ fontSize: 11, marginTop: 6, paddingLeft: 18, color: '#991b1b' }}>
                  {error.details.map((d, i) => (
                    <li key={i}>📍 <code style={{ background: '#fee', padding: '0 4px' }}>{d.property}</code>: {d.message}</li>
                  ))}
                </ul>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                💡 ลองคลิก Template ด้านบนเพื่อเริ่มต้นด้วย JSON ที่ใช้งานได้
              </div>
            </div>
          )}

          <div className="form-actions" style={{ marginTop: 12 }}>
            <button className="btn-secondary" onClick={onClose}>ยกเลิก</button>
            <button className="btn-primary" onClick={send} disabled={sending}>
              {sending ? 'กำลังส่ง...' : 'ส่ง Flex'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderContent(content) {
  if (!content) return content;
  const LE_RE = /\[LE:([^:]+):([^\]]+)\]/g;
  if (!LE_RE.test(content)) return content;
  LE_RE.lastIndex = 0;
  const parts = [];
  let last = 0, m;
  while ((m = LE_RE.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(<img key={m.index}
      src={`https://stickershop.line-scdn.net/sticonshop/v1/sticon/${m[1]}/android/${m[2]}.png`}
      alt="emoji" className="chat-line-emoji"
      onError={e => { e.currentTarget.style.display = 'none'; }} />);
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts;
}

function Bubble({ msg, onContextMenu, pictureUrl, displayName, showAvatar = false }) {
  const isOut = msg.direction === 'outbound';

  if (msg.is_private_note) {
    return (
      <div className="msg-row note-row">
        <div className="note-bubble-wrap">
          <div className="note-label">
            📝 Note ภายใน{msg.agent_name ? ` · ${msg.agent_name}` : ''}
          </div>
          <div className="bubble-note">{msg.content}</div>
          <div className="msg-time">{dayjs(msg.created_at).format('HH:mm')}</div>
        </div>
      </div>
    );
  }

  if (msg.message_type === 'auto_reply') {
    return (
      <div className="msg-row note-row">
        <div className="note-bubble-wrap">
          <div className="note-label auto-reply-label">🤖 ระบบ Auto-reply</div>
          <div className="bubble-auto-reply">{msg.content}</div>
          <div className="msg-time">{dayjs(msg.created_at).format('HH:mm')}</div>
        </div>
      </div>
    );
  }

  function handleContextMenu(e) {
    e.preventDefault();
    onContextMenu?.({ x: e.clientX, y: e.clientY, content: msg.content });
  }

  return (
    <div className={`msg-row ${isOut ? 'outbound' : 'inbound'}`}>
      {!isOut && (
        showAvatar
          ? (pictureUrl
              ? <img src={pictureUrl} alt={displayName} className="msg-avatar" />
              : <div className="msg-avatar msg-avatar-fallback">{(displayName || '?').slice(0, 1)}</div>)
          : <div className="msg-avatar msg-avatar-placeholder" />
      )}
      <div className="bubble-wrap">
        {isOut && msg.agent_name && (
          <div className="msg-sender">{msg.agent_name}</div>
        )}
        <div
          className={`bubble ${isOut ? 'bubble-out' : 'bubble-in'} ${msg.media_url ? 'bubble-image-wrap' : ''}`}
          onContextMenu={handleContextMenu}
        >
          {msg.message_type === 'sticker' && msg.media_url ? (
            <img src={msg.media_url} alt="สติกเกอร์" className="chat-sticker" />
          ) : msg.message_type === 'video' && msg.media_url ? (
            <video
              src={msg.media_url}
              controls
              preload="metadata"
              className="chat-video"
            />
          ) : msg.message_type === 'video' ? (
            <div className="chat-video-loading">⏳ กำลังโหลดวิดีโอ...</div>
          ) : msg.media_url ? (
            <img
              src={msg.media_url}
              alt="รูปภาพ"
              className="chat-image"
              onClick={() => window.open(msg.media_url, '_blank')}
              title="คลิกเพื่อดูขนาดเต็ม"
            />
          ) : renderContent(msg.content)}
        </div>
        <div className="msg-time">{dayjs(msg.created_at).format('HH:mm')}</div>
      </div>
    </div>
  );
}

function TagPicker({ conversationId, currentTags = [], allTags = [], onTagsChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function toggle(tag) {
    const has = currentTags.some(t => t.id === tag.id);
    try {
      if (has) {
        await api.removeConversationTag(conversationId, tag.id);
        onTagsChange(currentTags.filter(t => t.id !== tag.id));
      } else {
        await api.addConversationTag(conversationId, tag.id);
        onTagsChange([...currentTags, tag]);
      }
    } catch (err) { alert(err.message); }
  }

  return (
    <div className="tag-picker-wrap" ref={ref}>
      {/* Current tags */}
      <div className="tag-list">
        {currentTags.map(t => (
          <span key={t.id} className="tag-pill" style={{ background: t.color + '22', color: t.color, borderColor: t.color + '55' }}>
            {t.name}
          </span>
        ))}
        {allTags.length > 0 && (
          <button className="tag-add-btn" onClick={() => setOpen(v => !v)} title="จัดการ Tags">
            {open ? '✕' : '+ Tag'}
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="tag-dropdown">
          {allTags.map(tag => {
            const active = currentTags.some(t => t.id === tag.id);
            return (
              <button key={tag.id} className={`tag-option ${active ? 'active' : ''}`} onClick={() => toggle(tag)}>
                <span className="tag-dot" style={{ background: tag.color }} />
                {tag.name}
                {active && <span className="tag-check">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ChatPanel({ conversation, oaAccounts, agents, allTags = [], quickReplies = [], onConversationUpdate, infoPanelOpen, onToggleInfo, onImportCustomerInfo, refreshToken = 0, onMobileBack }) {
  const { agent: me } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [mode, setMode] = useState('reply'); // 'reply' | 'note'
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, content }
  const [imagePreview, setImagePreview] = useState(null); // { file, url }
  const [sendingImage, setSendingImage] = useState(false);
  const [showStickers, setShowStickers]   = useState(false);
  const [showFlex, setShowFlex]           = useState(false);
  const [sendingSticker, setSendingSticker] = useState(false);
  // 1-second ticker for the reply-token countdown banner + Send button state.
  // Only runs while there's an active reply window (>0) or during the last
  // 16 minutes of it — the banner itself hides above 15 min anyway.
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    if (!conversation?.reply_expires_at) return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [conversation?.reply_expires_at]);
  const replyExpired = !conversation?.reply_expires_at ||
    new Date(conversation.reply_expires_at).getTime() <= nowTs;
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);
  const quickRepliesRef = useRef(quickReplies);
  quickRepliesRef.current = quickReplies;
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  // Close context menu on click outside
  useEffect(() => {
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Document-level Alt+1~9 listener — works regardless of textarea focus
  // Uses e.code (Digit1~9) for cross-platform compatibility (macOS Alt sends ¡ as e.key)
  useEffect(() => {
    function handleGlobalKeyDown(e) {
      if (!e.altKey) return;
      if (!/^Digit[1-9]$/.test(e.code)) return;
      if (!conversationRef.current) return;
      e.preventDefault();
      const num = parseInt(e.code.slice(-1), 10);
      const qr = quickRepliesRef.current.find(q => q.key_number === num);
      if (qr) {
        setText(qr.content);
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
    }
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const oa = oaAccounts.find(o => o.id === conversation?.oa_id);

  // Load messages when conversation changes
  useEffect(() => {
    if (!conversation) return;
    setLoading(true);
    setMessages([]);
    api.getMessages(conversation.id)
      .then(setMessages)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [conversation?.id]); // eslint-disable-line

  // WS reconnect — reload to catch messages missed during disconnect
  useEffect(() => {
    if (!refreshToken || !conversation?.id) return;
    api.getMessages(conversation.id)
      .then(newMsgs => {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const fresh = newMsgs.filter(m => !existingIds.has(m.id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });
      })
      .catch(console.error);
  }, [refreshToken]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const appendMessage = useCallback((msg) => {
    setMessages(prev => {
      if (prev.find(m => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  // Update existing message (e.g. media_url set after image download)
  const updateMessage = useCallback((updated) => {
    setMessages(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
  }, []);

  useEffect(() => {
    if (conversation) {
      conversation._appendMessage = appendMessage;
      conversation._updateMessage = updateMessage;
    }
  }, [conversation, appendMessage, updateMessage]);

  async function handleSend() {
    if (!text.trim() || !conversation || sending) return;
    setSending(true);
    try {
      let msg;
      if (mode === 'note') {
        msg = await api.createNote(conversation.id, text.trim());
      } else {
        msg = await api.sendMessage(conversation.id, text.trim());
        onConversationUpdate?.({ ...conversation, last_message: text.trim(), last_message_at: new Date() });
      }
      setText('');
      setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
    } catch (err) {
      alert((mode === 'note' ? 'บันทึก Note ไม่สำเร็จ: ' : 'ส่งข้อความไม่สำเร็จ: ') + err.message);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImagePreview({ file, url: URL.createObjectURL(file) });
    e.target.value = '';
  }

  async function handleSendImage() {
    if (!imagePreview || !conversation || sendingImage) return;
    setSendingImage(true);
    try {
      const msg = await api.sendImage(conversation.id, imagePreview.file);
      appendMessage(msg);
      URL.revokeObjectURL(imagePreview.url);
      setImagePreview(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingImage(false);
    }
  }

  // LINE Messaging API Type-1 (free) sticker packages
  // Ref: https://developers.line.biz/en/docs/messaging-api/sticker-list/
  const THANK_STICKER = { p: '1', s: '2' };

  const STICKERS = [
    // Package 1 — Moon/LINE Friends
    { p:'1', s:'1'  }, { p:'1', s:'2'  }, { p:'1', s:'3'  }, { p:'1', s:'4'  },
    { p:'1', s:'5'  }, { p:'1', s:'6'  }, { p:'1', s:'7'  }, { p:'1', s:'8'  },
    { p:'1', s:'9'  }, { p:'1', s:'10' }, { p:'1', s:'11' }, { p:'1', s:'12' },
    { p:'1', s:'13' }, { p:'1', s:'14' }, { p:'1', s:'15' }, { p:'1', s:'16' },
    // Package 2 — Brown
    { p:'2', s:'15' }, { p:'2', s:'17' }, { p:'2', s:'21' }, { p:'2', s:'27' },
    { p:'2', s:'28' }, { p:'2', s:'31' }, { p:'2', s:'32' }, { p:'2', s:'34' },
    // Package 3 — Cony
    { p:'3', s:'180'}, { p:'3', s:'181'}, { p:'3', s:'182'}, { p:'3', s:'183'},
    { p:'3', s:'184'}, { p:'3', s:'185'}, { p:'3', s:'186'}, { p:'3', s:'187'},
    // Package 4 — James
    { p:'4', s:'1'  }, { p:'4', s:'2'  }, { p:'4', s:'3'  }, { p:'4', s:'4'  },
    { p:'4', s:'5'  }, { p:'4', s:'6'  }, { p:'4', s:'7'  }, { p:'4', s:'8'  },
  ];

  async function handleSendSticker(packageId, stickerId) {
    if (!conversation || sendingSticker) return;
    setSendingSticker(true);
    setShowStickers(false);
    try {
      const msg = await api.sendSticker(conversation.id, packageId, stickerId);
      appendMessage(msg);
    } catch (err) { alert(err.message); }
    finally { setSendingSticker(false); }
  }

  async function handleThankAndClose() {
    if (!conversation || sendingSticker) return;
    setSendingSticker(true);
    try {
      const msg = await api.sendSticker(conversation.id, THANK_STICKER.p, THANK_STICKER.s);
      appendMessage(msg);
      const updated = await api.updateStatus(conversation.id, 'closed');
      onConversationUpdate?.(updated);
    } catch (err) { alert(err.message); }
    finally { setSendingSticker(false); }
  }

  function handlePaste(e) {
    if (mode !== 'reply') return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) setImagePreview({ file, url: URL.createObjectURL(file) });
        return;
      }
    }
  }

  async function handleAssign(agentId) {
    try {
      const updated = await api.assignAgent(conversation.id, agentId || null);
      onConversationUpdate?.(updated);
    } catch (err) { alert(err.message); }
  }

  async function handleStatus(status) {
    try {
      const updated = await api.updateStatus(conversation.id, status);
      onConversationUpdate?.(updated);
    } catch (err) { alert(err.message); }
  }

  function handleTagsChange(newTags) {
    onConversationUpdate?.({ ...conversation, tags: newTags });
  }

  if (!conversation) {
    return (
      <main className="chat-panel empty-panel">
        <div className="changelog-wrap">
          <div className="changelog-header">
            <div className="changelog-logo">
              <svg width="40" height="40" viewBox="0 0 200 200" fill="none">
                <rect x="22" y="22" width="156" height="156" rx="24" fill="#1E2433" stroke="#2A3345" strokeWidth="1.5"/>
                <rect x="32" y="32" width="68" height="62" rx="12" fill="#FF6347" opacity="0.9"/>
                <rect x="100" y="32" width="68" height="62" rx="12" fill="#00D9A6" opacity="0.9"/>
                <rect x="32" y="98" width="68" height="62" rx="12" fill="#5B8DEF" opacity="0.9"/>
                <rect x="100" y="98" width="68" height="62" rx="12" fill="#FFB347" opacity="0.9"/>
                <circle cx="100" cy="100" r="10" fill="#0C0F14" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                <circle cx="100" cy="100" r="4" fill="white" opacity="0.9"/>
              </svg>
              <div>
                <div className="changelog-brand"><span>Merge</span><span style={{color:'#FF6347'}}>Chat</span></div>
                <div className="changelog-version">Release Notes</div>
              </div>
            </div>
            <p className="changelog-desc">เลือกแชทจากซ้ายมือเพื่อเริ่มตอบลูกค้า</p>
          </div>

          <div className="changelog-list">

            <div className="cl-item">
              <div className="cl-badge cl-badge-new">ใหม่</div>
              <div className="cl-content">
                <div className="cl-title">📊 ป้องกันโควต้า Push เกิน</div>
                <ul className="cl-detail">
                  <li>เปิดส่งแบบ push กลับ — เมื่อไม่มี reply token (เกิน 55 นาที) ยังส่งข้อความหาลูกค้าได้</li>
                  <li>⚠️ เตือนเมื่อโควต้า push ใกล้หมด (90% ของลิมิตจริงต่อ OA) ทั้งใน Telegram และแบนเนอร์ในแอป</li>
                  <li>🛑 หยุดส่ง push อัตโนมัติเมื่อเต็มลิมิตจริง — ตอบแบบ reply (ฟรี) ได้ตามปกติ</li>
                  <li>อ่านลิมิตจริงจาก LINE ต่อ OA (ฟรี 500 / จ่ายเงินหลักหมื่น) — รีเซ็ตอัตโนมัติเมื่อขึ้นเดือนใหม่</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-new">V2.2</div>
              <div className="cl-content">
                <div className="cl-title">😊 Sticker + 🚫 Spam Filter</div>
                <ul className="cl-detail">
                  <li>ส่งสติกเกอร์ LINE ให้ลูกค้าได้จากปุ่ม 😊 ในช่องแชท</li>
                  <li>Right-click → 🚫 ทำเครื่องหมายสแปม — ซ่อนแชทที่ป่วน/spam</li>
                  <li>Tab 🚫 สแปม แยกเก็บไว้ ไม่ถูกลบ ยกเลิกสแปมได้</li>
                  <li>ลูกค้าสแปมส่งข้อความมาอีกก็ไม่ auto-reopen ขึ้นมารบกวน</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">V2 Phase 1</div>
              <div className="cl-content">
                <div className="cl-title">🤖 AI Bot — Shadow Mode</div>
                <ul className="cl-detail">
                  <li>Claude AI ประมวลผลแบบ parallel ทุกข้อความที่ลูกค้าส่ง</li>
                  <li>Agent เห็นคำแนะนำ Claude พร้อม action (auto_send / draft / escalate)</li>
                  <li>กด 👍/👎 เพื่อ rate ความแม่นยำ — เก็บ stats อัตโนมัติ</li>
                  <li>กด "↗ ใช้ข้อความนี้" เพื่อนำ draft ของ Claude ไปพิมพ์</li>
                  <li>Settings → 🤖 AI Bot — ตั้ง mode + FAQ knowledge base ต่อ OA</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 6</div>
              <div className="cl-content">
                <div className="cl-title">👤 User Info Panel</div>
                <ul className="cl-detail">
                  <li>กดปุ่ม 👤 ที่หัวแชท — panel ขยายออกทางขวา (push layout)</li>
                  <li>บันทึก ชื่อจริง, เบอร์โทร, บัญชีธนาคาร (หลายบัญชีได้)</li>
                  <li>คลิกขวาบน bubble → "📥 นำเข้าข้อมูลลูกค้า" → auto-parse</li>
                  <li>🔄 Sync placeholder พร้อมรองรับ external API ในอนาคต</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 5.5</div>
              <div className="cl-content">
                <div className="cl-title">🤖 Auto-reply ทักทายลูกค้า</div>
                <ul className="cl-detail">
                  <li>ตั้งได้ต่อ OA — เปิด/ปิด + ข้อความเอง</li>
                  <li>ส่งอัตโนมัติเมื่อลูกค้าทักข้อความแรกของวัน (00:00 น. ไทย)</li>
                  <li>ใช้ reply token (ฟรี) ตอบทันทีใน 30 วินาที</li>
                  <li>แสดงเป็น 🤖 ระบบ bubble สีน้ำเงินใน chat timeline</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 5</div>
              <div className="cl-content">
                <div className="cl-title">📋 Reports — Agent Performance</div>
                <ul className="cl-detail">
                  <li>Leaderboard 🥇🥈🥉 จัดอันดับ agent ตาม performance</li>
                  <li>ตาราง: ข้อความส่ง, เคสที่ปิด, Avg. ตอบแรก, Avg. ปิดเคส</li>
                  <li>Color-coded response time — เขียว ≤ 15น / เหลือง ≤ 30น / แดง &gt; 30น</li>
                  <li>Export CSV พร้อม BOM รองรับภาษาไทย</li>
                  <li>เลือก period 7 / 30 / 90 วัน + sort ทุก column</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 4</div>
              <div className="cl-content">
                <div className="cl-title">📊 Dashboard</div>
                <ul className="cl-detail">
                  <li>Stat cards — ข้อความเข้า/ออก, สนทนา, ผู้ใหม่ + trend เปรียบเทียบ period ก่อนหน้า</li>
                  <li>Bar chart ปริมาณข้อความรายวัน (inbound vs outbound)</li>
                  <li>Peak Hours — ชั่วโมงที่ลูกค้าทักมากสุด</li>
                  <li>OA Performance, Status Distribution, Team Activity</li>
                  <li>เลือก period 7 / 30 / 90 วัน — Permission-aware (เห็นเฉพาะ OA ที่มีสิทธิ์)</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 3.5</div>
              <div className="cl-content">
                <div className="cl-title">📝 Private Notes</div>
                <ul className="cl-detail">
                  <li>พิมพ์ Note ภายในที่ลูกค้าไม่เห็น</li>
                  <li>Toggle [💬 ตอบลูกค้า] / [📝 Note ภายใน] ในช่องพิมพ์</li>
                  <li>Note แสดงใน timeline สีอำพัน — broadcast real-time ให้ agent คนอื่น</li>
                  <li>รองรับ Command System ในอนาคต</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 3</div>
              <div className="cl-content">
                <div className="cl-title">🔐 OA Access Control</div>
                <ul className="cl-detail">
                  <li>Admin กำหนดว่า Agent แต่ละคนเห็น OA ไหนได้</li>
                  <li>Backend กรองข้อมูล conversations + OA อัตโนมัติ</li>
                  <li>Settings → จัดการสิทธิ์ (admin only)</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 2</div>
              <div className="cl-content">
                <div className="cl-title">⚡ Quick Replies + 🏷 Customer Tags</div>
                <ul className="cl-detail">
                  <li>กด Alt+1~9 เพื่อส่งข้อความสำเร็จรูป (per-agent)</li>
                  <li>คลิก hint bar หรือกด shortcut ใส่ข้อความทันที</li>
                  <li>สร้าง Tag สีสันสำหรับลูกค้า เช่น ลูกค้าดีเด่น, ลูกค้าเก่า</li>
                  <li>Tags แสดงใน conversation list และ chat header</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Phase 1</div>
              <div className="cl-content">
                <div className="cl-title">🔒 Security & Tracking</div>
                <ul className="cl-detail">
                  <li>JWT จริง (HS256, 7-day expiry) แทน base64 token เดิม</li>
                  <li>บันทึก Agent ID ทุกครั้งที่ส่งข้อความ</li>
                  <li>ติดตาม first_response_at, closed_at, closed_by</li>
                  <li>Token หมดอายุ → Auto logout อัตโนมัติ</li>
                </ul>
              </div>
            </div>

            <div className="cl-item">
              <div className="cl-badge cl-badge-done">Foundation</div>
              <div className="cl-content">
                <div className="cl-title">🚀 Deploy & Infrastructure</div>
                <ul className="cl-detail">
                  <li>Caddy reverse proxy — SSL อัตโนมัติจาก Let's Encrypt</li>
                  <li>WebSocket real-time (auto-reconnect, per-OA subscribe)</li>
                  <li>Auto-refresh inbox เมื่อมีข้อความใหม่</li>
                  <li>MergeChat branding + Green design system</li>
                </ul>
              </div>
            </div>

          </div>

          <div className="changelog-footer">
            Roadmap ถัดไป: V2 Phase 2 Draft Mode · Phase 3 Auto Mode
          </div>
        </div>
      </main>
    );
  }

  const assignedAgent = agents.find(a => a.id === conversation.assigned_agent_id);
  const statusColors = { open: '#16a34a', pending: '#ea580c', closed: '#94a3b8' };

  return (
    <main className="chat-panel">
      {/* Right-edge toggle handle */}
      <button
        className={`info-edge-handle ${infoPanelOpen ? 'open' : ''}`}
        onClick={e => { e.stopPropagation(); onToggleInfo?.(); }}
        title={infoPanelOpen ? 'ปิดข้อมูลลูกค้า' : 'ดูข้อมูลลูกค้า'}
      >
        {infoPanelOpen ? '▶' : '◀'}
        <span className="handle-label">ข้อมูล</span>
      </button>

      {/* Header */}
      <div className="chat-header">
        <div className="header-left">
          <button className="btn-mobile-back" onClick={onMobileBack}>‹</button>
          <div className="header-avatar" style={{ background: oa ? oa.color + '22' : '#f0fdf4', color: oa?.color || '#16a34a' }}>
            {(conversation.display_name || '?').slice(0, 2)}
          </div>
          <div>
            <div className="header-name">{conversation.display_name || 'ผู้ใช้ไม่ระบุชื่อ'}</div>
            <div className="header-sub">
              {oa && <span className="oa-dot" style={{ background: oa.color }} />}
              {oa?.name || 'OA ไม่ระบุ'}
              <span className="status-label" style={{ color: statusColors[conversation.status] }}>
                · {conversation.status === 'open' ? 'เปิด' : conversation.status === 'pending' ? 'รอดำเนินการ' : 'ปิดแล้ว'}
              </span>
            </div>
            {/* Tags */}
            <TagPicker
              conversationId={conversation.id}
              currentTags={conversation.tags || []}
              allTags={allTags}
              onTagsChange={handleTagsChange}
            />
          </div>
        </div>
        <div className="header-actions">
          <select
            className="agent-select-sm"
            value={conversation.assigned_agent_id || ''}
            onChange={e => handleAssign(e.target.value)}
            title="มอบหมาย Agent"
          >
            <option value="">— มอบหมาย —</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          {conversation.status !== 'closed' ? (
            <button className="btn-action btn-close" onClick={() => handleStatus('closed')}>✓ ปิดแชท</button>
          ) : (
            <button className="btn-action btn-reopen" onClick={() => handleStatus('open')}>↩ เปิดใหม่</button>
          )}
          <button
            className={`btn-info-toggle ${infoPanelOpen ? 'active' : ''}`}
            onClick={onToggleInfo}
            title="ข้อมูลลูกค้า"
          >
            👤
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="messages-area">
        {loading && <div className="loading-msgs">กำลังโหลด...</div>}
        {messages.map((msg, i) => {
          const next = messages[i + 1];
          const prev = messages[i - 1];
          const showAvatar = msg.direction === 'inbound' &&
            (!next || next.direction !== 'inbound');
          const showDateSep = !prev || !dayjs(prev.created_at).isSame(msg.created_at, 'day');
          return (
            <Fragment key={msg.id}>
              {showDateSep && (
                <div className="date-sep-row">
                  <span className="date-sep-pill">{formatDateSeparator(msg.created_at)}</span>
                </div>
              )}
              <Bubble
                msg={msg}
                onContextMenu={({ x, y, content }) => setContextMenu({ x, y, content })}
                pictureUrl={conversation?.picture_url}
                displayName={conversation?.display_name}
                showAvatar={showAvatar}
              />
            </Fragment>
          );
        })}
        <div ref={bottomRef} />

        {/* Right-click context menu */}
        {contextMenu && (
          <div
            className="chat-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={e => e.stopPropagation()}
          >
            <button
              className="ctx-item ctx-item-primary"
              onClick={() => {
                const parsed = parseCustomerInfo(contextMenu.content);
                onImportCustomerInfo?.(parsed);
                if (!infoPanelOpen) onToggleInfo?.();
                setContextMenu(null);
              }}
            >
              📥 นำเข้าข้อมูลลูกค้า
            </button>
            <div className="ctx-divider" />
            <button
              className="ctx-item"
              onClick={() => {
                navigator.clipboard.writeText(contextMenu.content).catch(() => {});
                setContextMenu(null);
              }}
            >
              📋 คัดลอก
            </button>
          </div>
        )}
      </div>

      {/* Compose */}
      <div className={`compose-area ${mode === 'note' ? 'compose-note-mode' : ''}`}>
        {/* Mode tabs */}
        <div className="compose-mode-tabs">
          <button
            className={`mode-tab ${mode === 'reply' ? 'active' : ''}`}
            onClick={() => setMode('reply')}
          >
            💬 ตอบลูกค้า
          </button>
          <button
            className={`mode-tab ${mode === 'note' ? 'active note-mode' : ''}`}
            onClick={() => setMode('note')}
          >
            📝 Note ภายใน
          </button>
          {mode === 'reply' && (
            <div style={{ marginLeft: 'auto' }}>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />
              <button
                className="btn-image"
                onClick={() => imageInputRef.current?.click()}
                disabled={conversation.status === 'closed'}
                title="ส่งรูปภาพ"
              >
                📎 แนบรูป
              </button>
            </div>
          )}
          {mode === 'reply' && (
            <div style={{ position: 'relative', marginLeft: 4 }}>
              <button
                className="btn-image"
                onClick={() => setShowStickers(v => !v)}
                disabled={conversation.status === 'closed' || sendingSticker}
                title="ส่งสติกเกอร์"
              >
                😊 สติกเกอร์
              </button>
              {showStickers && (
                <>
                  <div className="ctx-overlay" onClick={() => setShowStickers(false)} />
                  <div className="sticker-panel">
                    {STICKERS.map(({p, s}) => (
                      <img
                        key={`${p}-${s}`}
                        src={`https://stickershop.line-scdn.net/stickershop/v1/sticker/${s}/android/sticker.png`}
                        alt="sticker"
                        className="sticker-thumb"
                        onClick={() => handleSendSticker(p, s)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {mode === 'reply' && (
            <>
              <div className="compose-tab-divider" />
              <button className="btn-image" onClick={() => setShowFlex(true)}
                disabled={conversation.status === 'closed'}
                title="ส่ง Flex Message">
                🎴 Flex
              </button>
              <button
                className="btn-quick-sticker btn-quick-close"
                onClick={handleThankAndClose}
                disabled={conversation.status === 'closed' || sendingSticker}
                title="ส่งขอบคุณ + ปิดเคส"
              >
                🙏 ขอบคุณ & ปิด
              </button>
            </>
          )}
        </div>

        {showFlex && (
          <FlexModal conversation={conversation} onClose={() => setShowFlex(false)} onSent={appendMessage} />
        )}

        {/* Quick Reply hint bar (reply mode only) */}
        {mode === 'reply' && quickReplies.length > 0 && (
          <div className="qr-hint-bar">
            {quickReplies.map(qr => (
              <span
                key={qr.key_number}
                className="qr-hint-chip"
                onClick={() => { setText(qr.content); textareaRef.current?.focus(); }}
                title={qr.content}
              >
                <kbd>Alt+{qr.key_number}</kbd> {qr.label}
              </span>
            ))}
          </div>
        )}

        {mode === 'reply' && conversation.status !== 'closed' && (
          <ReplyExpiryBanner expiresAt={conversation.reply_expires_at} nowTs={nowTs} />
        )}

        <div className="compose-meta">
          <span className="compose-as">
            {mode === 'note' ? '🔒 Note นี้ลูกค้าไม่เห็น — ' : ''}
            ในนามของ: <strong>{me?.name || 'คุณ'}</strong>
          </span>
          {mode === 'reply' && assignedAgent && assignedAgent.id !== me?.id && (
            <span className="compose-assigned">มอบหมายให้: {assignedAgent.name}</span>
          )}
        </div>

        <div className="compose-row">
          <textarea
            ref={textareaRef}
            className={`compose-input ${mode === 'note' ? 'note-input' : ''}`}
            placeholder={
              conversation.status === 'closed' && mode === 'reply'
                ? 'แชทนี้ปิดแล้ว — กด "เปิดใหม่" เพื่อตอบ'
                : mode === 'note'
                  ? 'พิมพ์ Note ภายใน... (ลูกค้าไม่เห็น)'
                  : 'พิมพ์ข้อความ... (Enter ส่ง, Alt+1~9 Quick Reply)'
            }
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={sending || (conversation.status === 'closed' && mode === 'reply')}
            rows={3}
          />
          <button
            className={`btn-send ${sending ? 'sending' : ''} ${mode === 'note' ? 'btn-note' : ''}`}
            onClick={handleSend}
            disabled={
              sending || !text.trim() ||
              (mode === 'reply' && conversation.status === 'closed') ||
              (mode === 'reply' && replyExpired)
            }
            title={
              mode === 'note' ? 'บันทึก Note'
                : (mode === 'reply' && replyExpired)
                  ? 'reply_token หมดอายุ — รอลูกค้าส่งข้อความใหม่'
                  : 'ส่งข้อความ'
            }
          >
            {sending ? '...' : mode === 'note' ? '📝' : '→'}
          </button>
        </div>
      </div>

      {/* Image preview modal */}
      {imagePreview && (
        <div className="image-send-overlay" onClick={() => { URL.revokeObjectURL(imagePreview.url); setImagePreview(null); }}>
          <div className="image-send-modal" onClick={e => e.stopPropagation()}>
            <div className="image-send-header">
              <span>ส่งรูปภาพนี้?</span>
              <button className="image-send-close" onClick={() => { URL.revokeObjectURL(imagePreview.url); setImagePreview(null); }}>✕</button>
            </div>
            <img src={imagePreview.url} alt="preview" className="image-send-preview" />
            <div className="image-send-actions">
              <button className="btn-secondary" onClick={() => { URL.revokeObjectURL(imagePreview.url); setImagePreview(null); }}>ยกเลิก</button>
              <button className="btn-primary" onClick={handleSendImage} disabled={sendingImage}>
                {sendingImage ? '⏳ กำลังส่ง...' : '📤 ส่งรูปนี้'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
